import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import supabase from '../../../utils/supabase.js';
import { candidateService } from '../../../services/supabaseService';
import candidateAnalyticsService from '../../../services/candidateAnalyticsService';
import { fetchAllSkillsFromDatabase, getAllSkills, processSkills } from '../../../utils/skillsUtils';
import {
  CANDIDATES_PER_PAGE,
  DEBOUNCE_DELAY,
  EXPERIENCE_LEVELS,
  AVAILABILITY_OPTIONS,
  ERROR_MESSAGES
} from '../constants/searchConstants';

/**
 * Custom hook for managing candidate search functionality
 * Encapsulates search logic, filtering, and data loading
 */
export const useCandidateSearch = () => {
  const { user, userProfile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchError, setSearchError] = useState(null);
  
  // Initialize with default skills, will be replaced with dynamic ones
  const [skillOptions, setSkillOptions] = useState(getAllSkills());
  
  // Search state
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [filters, setFilters] = useState({
    skills: searchParams.get('skills')?.split(',').filter(Boolean) || [],
    experience_level: searchParams.get('experience') || '',
    hourly_rate_min: searchParams.get('min_rate') || '',
    hourly_rate_max: searchParams.get('max_rate') || '',
    location: searchParams.get('location') || '',
    availability: searchParams.get('availability') || '',
    rating_min: searchParams.get('min_rating') || ''
  });
  
  // Results state
  const [candidates, setCandidates] = useState([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  
  // UI state
  const [savedCandidates, setSavedCandidates] = useState([]);
  const [viewMode, setViewMode] = useState(
    localStorage.getItem('candidateViewMode') || 'list'
  );
  const [showFilters, setShowFilters] = useState(true);
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  
  // Refs for performance
  const abortControllerRef = useRef(null);
  
  // Debounced search query
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  
  // Debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, DEBOUNCE_DELAY);
    
    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);
  
  // Load candidates with optimized caching and error handling
  const loadCandidates = useCallback(async (isInitialLoad = false, page = 1) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller if supported
    if (typeof window !== 'undefined' && window.AbortController) {
      abortControllerRef.current = new window.AbortController();
    }
    
    if (isInitialLoad) {
      setLoading(true);
    } else if (page > 1) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    setSearchError(null);
    
    try {
      // If showing saved only, filter to saved candidates
      if (showSavedOnly) {
        if (savedCandidates.length === 0) {
          // No saved candidates
          setCandidates([]);
          setTotalCandidates(0);
          setHasNextPage(false);
          return [];
        }
        
        // Get saved candidates data
        const { data: savedCandidatesData, error: savedError } = await supabase
          .from('user_profiles')
          .select(`
            id,
            full_name,
            email,
            bio,
            skills,
            experience_years,
            hourly_rate,
            location,
            availability,
            profile_picture,
            rating,
            total_reviews,
            is_verified,
            identity_verification_status,
            created_at
          `)
          .in('id', savedCandidates)
          .eq('role', 'va')
          .range((page - 1) * CANDIDATES_PER_PAGE, page * CANDIDATES_PER_PAGE - 1);
        
        if (savedError) throw new Error(savedError.message);
        
        const savedResults = savedCandidatesData || [];
        
        // Update state
        if (page === 1) {
          setCandidates(savedResults);
        } else {
          setCandidates(prev => [...prev, ...savedResults]);
        }
        
        setTotalCandidates(savedCandidates.length);
        setHasNextPage(savedCandidates.length > page * CANDIDATES_PER_PAGE);
        
        return savedResults;
      }
      
      const searchFilters = {
        query: debouncedSearchQuery.trim(),
        skills: filters.skills,
        experience_level: filters.experience_level,
        hourly_rate_min: filters.hourly_rate_min ? Number(filters.hourly_rate_min) : null,
        hourly_rate_max: filters.hourly_rate_max ? Number(filters.hourly_rate_max) : null,
        location: filters.location,
        availability: filters.availability,
        rating_min: filters.rating_min ? Number(filters.rating_min) : null,
        limit: CANDIDATES_PER_PAGE,
        offset: (page - 1) * CANDIDATES_PER_PAGE,
        sort_by: 'updated_at',
        sort_order: 'desc',
        ...(abortControllerRef.current && { signal: abortControllerRef.current.signal })
      };
      
      const result = await candidateService.searchCandidates(searchFilters);
      
      if (!result) {
        throw new Error(ERROR_MESSAGES.LOAD_FAILED);
      }
      
      const { candidates: searchResults = [], total = 0, hasMore = false, error } = result;
      
      if (error) throw new Error(error);
      
      // Update state
      if (page === 1) {
        setCandidates(searchResults);
      } else {
        setCandidates(prev => [...prev, ...searchResults]);
      }
      
      setTotalCandidates(total);
      setHasNextPage(hasMore);
      
      return searchResults;
    } catch (error) {
      if (error.name !== 'AbortError') {
        setSearchError(error.message || ERROR_MESSAGES.LOAD_FAILED);
        setCandidates([]);
        setTotalCandidates(0);
        setHasNextPage(false);
      }
      return [];
    } finally {
      setLoading(false);
      setLoadingMore(false);
      if (isInitialLoad) setInitialLoading(false);
    }
  }, [debouncedSearchQuery, filters, showSavedOnly, savedCandidates]);
  
  // Load saved candidates
  const loadSavedCandidates = useCallback(async () => {
    if (!user?.id) {
      console.warn('Cannot load saved candidates: No authenticated user');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('saved_candidates')
        .select('candidate_id')
        .eq('employer_id', user.id);
      
      if (error) {
        console.error('Error loading saved candidates:', error);
        
        // Check if the table exists - if not, it might need to be created
        if (error.code === '42P01') { // PostgreSQL code for undefined_table
          console.warn('Saved candidates table does not exist, creating it now');
          
          try {
            // Create the table if it doesn't exist (this would normally be a migration)
            const { error: createError } = await supabase.rpc('create_saved_candidates_if_not_exists');
            if (createError) throw createError;
            
            // Try loading again
            const { data: retryData } = await supabase
              .from('saved_candidates')
              .select('candidate_id')
              .eq('employer_id', user.id);
              
            if (retryData && Array.isArray(retryData)) {
              setSavedCandidates(retryData.map(item => item.candidate_id));
            }
          } catch (createTableError) {
            console.error('Failed to create saved_candidates table:', createTableError);
          }
        }
        return;
      }
      
      if (Array.isArray(data)) {
        setSavedCandidates(data.map(item => item.candidate_id));
      }
    } catch (error) {
      console.error('Error in loadSavedCandidates:', error);
    }
  }, [user?.id]);
  
  // Toggle save/unsave candidate
  const toggleSaveCandidate = useCallback(async (candidateId) => {
    if (!user?.id) {
      console.warn('User must be authenticated to save candidates');
      
      if (window.toastify?.error) {
        window.toastify.error(ERROR_MESSAGES.AUTH_REQUIRED);
      }
      return;
    }
    
    // Check if user has proper permissions
    if (userProfile?.role !== 'employer' && userProfile?.role !== 'admin') {
      console.warn('User does not have employer permissions');
      
      if (window.toastify?.error) {
        window.toastify.error(ERROR_MESSAGES.EMPLOYER_ONLY);
      }
      return;
    }
    
    const isSaved = savedCandidates.includes(candidateId);
    
    // Track save/unsave event
    candidateAnalyticsService.trackSaveCandidate(candidateId, !isSaved);
    
    // Optimistic update
    setSavedCandidates(prev => 
      isSaved 
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
    
    try {
      if (isSaved) {
        const { error } = await supabase
          .from('saved_candidates')
          .delete()
          .eq('employer_id', user.id)
          .eq('candidate_id', candidateId);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('saved_candidates')
          .insert({
            employer_id: user.id,
            candidate_id: candidateId,
            created_at: new Date().toISOString()
          });
        
        if (error) throw error;
      }
    } catch (error) {
      // Revert optimistic update on error
      setSavedCandidates(prev => 
        isSaved 
          ? [...prev, candidateId]
          : prev.filter(id => id !== candidateId)
      );
      
      console.error('Error toggling saved candidate:', error);
      
      if (window.toastify?.error) {
        window.toastify.error(ERROR_MESSAGES.SAVE_FAILED);
      }
    }
  }, [user?.id, userProfile?.role, savedCandidates]);
  
  // Load more candidates for infinite scroll
  const loadMoreCandidates = useCallback(async () => {
    if (!hasNextPage || loading || loadingMore) return;
    
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    
    const results = await loadCandidates(false, nextPage);
    
    // Track load more event
    candidateAnalyticsService.trackLoadMore(nextPage, candidates.length + results.length);
  }, [currentPage, hasNextPage, loading, loadingMore, loadCandidates, candidates.length]);
  
  // Go to specific page
  const goToPage = useCallback(async (pageNum) => {
    if (pageNum < 1 || (pageNum > 1 && !hasNextPage && pageNum > currentPage)) return;
    
    setCurrentPage(pageNum);
    await loadCandidates(false, pageNum);
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [loadCandidates, hasNextPage, currentPage]);
  
  // Filter handlers
  const handleSkillToggle = useCallback((skill) => {
    console.log('Toggling skill:', skill);
    // Process input skill in case it contains commas
    const processedSkills = processSkills(skill);
    console.log('Processed skills:', processedSkills);
    
    setFilters(prev => {
      // Get current skills
      const currentSkills = [...prev.skills];
      console.log('Current skills before toggle:', currentSkills);
      
      // For each processed skill
      processedSkills.forEach(processedSkill => {
        if (currentSkills.includes(processedSkill)) {
          // Remove the skill if it's already included
          const index = currentSkills.indexOf(processedSkill);
          if (index !== -1) {
            currentSkills.splice(index, 1);
            console.log(`Removed skill: ${processedSkill}`);
          }
        } else {
          // Add the skill if it's not included
          currentSkills.push(processedSkill);
          console.log(`Added skill: ${processedSkill}`);
        }
      });
      
      console.log('Final skills after toggle:', currentSkills);
      
      // Track filter use
      candidateAnalyticsService.trackFilterUse('skills', currentSkills.join(','));
      
      return {
        ...prev,
        skills: currentSkills
      };
    });
  }, []);
  
  const handleFilterChange = useCallback((filterName, value) => {
    console.log(`Changing filter ${filterName} to:`, value);
    // Track filter use
    candidateAnalyticsService.trackFilterUse(filterName, value);
    
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  }, []);
  
  const clearAllFilters = useCallback(() => {
    // Track clear filters event
    candidateAnalyticsService.trackClearFilters();
    
    setFilters({
      skills: [],
      experience_level: '',
      hourly_rate_min: '',
      hourly_rate_max: '',
      location: '',
      availability: '',
      rating_min: ''
    });
    setSearchQuery('');
    setSearchParams({});
  }, [setSearchParams]);
  
  // Toggle saved only view
  const toggleSavedOnly = useCallback(() => {
    setShowSavedOnly(prev => {
      const newValue = !prev;
      if (newValue) {
        // When switching to saved only, reset to first page
        setCurrentPage(1);
      }
      return newValue;
    });
  }, []);
  
  // Initialize component
  useEffect(() => {
    const initializeComponent = async () => {
      setInitialLoading(true);
      
      try {
        // Load all skills including custom ones
        const allSkills = await fetchAllSkillsFromDatabase();
        setSkillOptions(allSkills);
        
        // Load initial data
        await Promise.all([
          loadSavedCandidates(),
          loadCandidates(true)
        ]);
      } catch (error) {
        console.error('Error initializing component:', error);
        setSearchError('Failed to load initial data. Please try refreshing the page.');
      } finally {
        setInitialLoading(false);
      }
    };

    // Only initialize once when we have user data
    if (user?.id && !initialLoading) {
      return; // Already initialized
    }
    
    if (user?.id) {
      initializeComponent();
    }
     
  }, [user?.id]); // Only depend on user ID
  
  // Auto-search when debounced query, filters, or saved only mode changes
  useEffect(() => {
    if (!initialLoading) {
      // Always perform the search, regardless of whether filters are applied
      const performSearch = async () => {
        setCurrentPage(1);
        
        // Update URL params
        const params = new URLSearchParams();
        if (debouncedSearchQuery) params.set('q', debouncedSearchQuery);
        if (filters.skills.length > 0) params.set('skills', filters.skills.join(','));
        if (filters.experience_level) params.set('experience', filters.experience_level);
        if (filters.hourly_rate_min) params.set('min_rate', filters.hourly_rate_min);
        if (filters.hourly_rate_max) params.set('max_rate', filters.hourly_rate_max);
        if (filters.location) params.set('location', filters.location);
        if (filters.availability) params.set('availability', filters.availability);
        if (filters.rating_min) params.set('min_rating', filters.rating_min);
        
        setSearchParams(params);
        
        const results = await loadCandidates(false, 1);
        
        // Track search analytics after results are loaded
        candidateAnalyticsService.trackSearch({
          query: debouncedSearchQuery,
          filters,
          resultsCount: results.length
        });
      };
      
      performSearch();
    }
     
  }, [debouncedSearchQuery, filters, showSavedOnly, initialLoading]);
  
  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('candidateViewMode', viewMode);
    
    // Track view mode change
    candidateAnalyticsService.trackViewModeChange(viewMode);
  }, [viewMode]);
  
  // Sync with user preferences
  useEffect(() => {
    if (userProfile?.preferences?.candidateViewMode) {
      setViewMode(userProfile.preferences.candidateViewMode);
    }
  }, [userProfile]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Load saved searches on mount
  useEffect(() => {
    if (user?.id) {
      loadSavedCandidates();
    }
  }, [user?.id, loadSavedCandidates]);
  
  return {
    // State
    loading,
    loadingMore,
    initialLoading,
    searchError,
    searchQuery,
    filters,
    candidates,
    totalCandidates,
    currentPage,
    hasNextPage,
    savedCandidates,
    viewMode,
    showFilters,
    showSavedOnly,
    user,
    userProfile,
    
    // Actions
    setSearchQuery,
    setViewMode,
    setShowFilters,
    toggleSaveCandidate,
    handleFilterChange,
    handleSkillToggle,
    loadMoreCandidates,
    goToPage,
    clearAllFilters,
    toggleSavedOnly,
    
    // Constants
    skillOptions,
    EXPERIENCE_LEVELS,
    AVAILABILITY_OPTIONS
  };
};

// Constants are now imported from searchConstants.js